// Default app config used when:
//   - A brand-new tenant signs up (seeded into app_config on first read).
//   - The mobile app boots offline and has no cached config.
//   - The admin clicks "Reset Defaults" in the App Builder.
//
// Designed to mirror what the web app does today, so a user who never opens
// the App Builder still gets a sensible mobile experience.

import type { AppConfig } from './schema';
import { ROLE_CATALOG } from './roles';

const DEFAULT_PRIMARY = '#1F6FFF';

export const DEFAULT_APP_CONFIG: AppConfig = {
  schemaVersion: 1,
  identity: {
    name: { fr: 'Mon ERP', en: 'My ERP', ar: 'إي آر بي' },
    shortName: 'Gesty',
    logoUrl: null,
    logoDarkUrl: null,
    splashLogoUrl: null,
    splashBgColor: DEFAULT_PRIMARY,
    tagline: { fr: 'Gestion commerciale tunisienne', en: 'Tunisian business management', ar: 'إدارة الأعمال التونسية' },
  },
  theme: {
    primaryColor: DEFAULT_PRIMARY,
    mode: 'system',
    radius: 12,
    font: 'Geist',
    density: 'comfortable',
  },
  bottomNav: [
    { id: 'nav-home', icon: 'home', label: { fr: 'Accueil', en: 'Home', ar: 'الرئيسية' },
      destination: 'dashboard', isFab: false, quickActions: [] },
    { id: 'nav-sales', icon: 'receipt', label: { fr: 'Ventes', en: 'Sales', ar: 'المبيعات' },
      destination: 'sales.invoices.list', isFab: false, quickActions: [
        { icon: 'plus', labelKey: 'shortcuts.newInvoice', destination: 'sales.invoices.form' },
        { icon: 'file-text', labelKey: 'shortcuts.newQuote', destination: 'sales.quotes.form' },
      ]},
    { id: 'nav-pos', icon: 'shopping-cart', label: { fr: 'Caisse', en: 'POS', ar: 'الصندوق' },
      destination: 'caisse.pos', isFab: true, quickActions: [] },
    { id: 'nav-clients', icon: 'users', label: { fr: 'Clients', en: 'Clients', ar: 'العملاء' },
      destination: 'clients.list', isFab: false, quickActions: [
        { icon: 'user-plus', labelKey: 'shortcuts.newClient', destination: 'clients.create' },
      ]},
    { id: 'nav-more', icon: 'menu', label: { fr: 'Plus', en: 'More', ar: 'المزيد' },
      destination: 'settings.hub', isFab: false, quickActions: [] },
  ],
  modules: {
    sales: true,
    purchases: true,
    stock: true,
    payments: true,
    treasury: false,
    checks: false,
    withholding: false,
    storefront: false,
    caisse: true,
  },
  screens: {},
  shortcuts: [
    { id: 'sc-invoice', icon: 'receipt', labelKey: 'shortcuts.newInvoice', target: 'sales.invoices.form' },
    { id: 'sc-quote',   icon: 'file-text', labelKey: 'shortcuts.newQuote', target: 'sales.quotes.form' },
    { id: 'sc-client',  icon: 'user-plus', labelKey: 'shortcuts.newClient', target: 'clients.create' },
    { id: 'sc-scan',    icon: 'scan-barcode', labelKey: 'shortcuts.scan', target: 'articles.scan' },
  ],
  roles: Object.fromEntries(
    ROLE_CATALOG.map((r) => [r.key, { allowedModules: r.allowedModules, bottomNavOverride: null }])
  ),
};

/** Lightweight "starter" config used for previews when no real config exists. */
export const STARTER_APP_CONFIG: AppConfig = {
  ...DEFAULT_APP_CONFIG,
  identity: { ...DEFAULT_APP_CONFIG.identity, name: { fr: 'Aperçu', en: 'Preview', ar: 'معاينة' } },
};
